import { normalizeRgbaHex, rgbaHexToTuple, rgbaTupleToHex } from "./color";
import { getPixel, inferSkinSize, pixelOffset, setPixel } from "./document";
import {
  SKIN_FACES,
  MAX_SKIN_SIZE,
  type PixelRect,
  type RgbaHex,
  type SkinDocument,
  type SkinFace,
  type SkinLayer,
  type SkinOperation,
  type SkinPart,
  type SkinSize,
  type SurfaceRef,
} from "./types";
import { getSurfaceSize, surfaceToUv } from "./uv";

export const MAX_OPERATION_PIXELS = MAX_SKIN_SIZE * MAX_SKIN_SIZE;

export class SkinOperationError extends Error {
  readonly code: "INVALID_OPERATION" | "PIXEL_LIMIT_EXCEEDED";

  constructor(
    message: string,
    code: SkinOperationError["code"] = "INVALID_OPERATION",
  ) {
    super(message);
    this.name = "SkinOperationError";
    this.code = code;
  }
}

export interface ApplyOperationsOptions {
  maxAffectedPixels?: number;
}

export interface ApplyOperationsResult {
  document: SkinDocument;
  affectedPixels: number;
}

/**
 * Applies a batch against a private copy. Any invalid operation or pixel-limit
 * breach throws without changing the input document or returning a partial edit.
 */
export function applySkinOperations(
  document: SkinDocument,
  operations: readonly SkinOperation[],
  options: ApplyOperationsOptions = {},
): ApplyOperationsResult {
  if (!Array.isArray(operations) || operations.length === 0) {
    throw new SkinOperationError("至少需要一个皮肤操作。");
  }
  const skinSize = document.size;
  const maxAffectedPixels = options.maxAffectedPixels ?? skinSize * skinSize;
  if (
    !Number.isInteger(maxAffectedPixels) ||
    maxAffectedPixels < 1 ||
    maxAffectedPixels > skinSize * skinSize
  ) {
    throw new SkinOperationError(
      `像素上限必须是 1 到 ${skinSize * skinSize} 的整数。`,
    );
  }

  const pixels = new Uint8ClampedArray(document.pixels);
  const changed = new Set<number>();

  const write = (x: number, y: number, color: RgbaHex): void => {
    assertPoint(x, y, skinSize);
    const normalized = normalizeRgbaHex(color);
    const offset = pixelOffset(x, y, skinSize);
    const [red, green, blue, alpha] = rgbaHexToTuple(normalized);
    if (
      pixels[offset] === red &&
      pixels[offset + 1] === green &&
      pixels[offset + 2] === blue &&
      pixels[offset + 3] === alpha
    ) {
      return;
    }
    pixels[offset] = red;
    pixels[offset + 1] = green;
    pixels[offset + 2] = blue;
    pixels[offset + 3] = alpha;
    changed.add(y * skinSize + x);
    if (changed.size > maxAffectedPixels) {
      throw new SkinOperationError(
        `本次操作影响的像素超过上限 ${maxAffectedPixels}。`,
        "PIXEL_LIMIT_EXCEEDED",
      );
    }
  };

  try {
    for (const operation of operations) {
      switch (operation.type) {
        case "pixel": {
          write(operation.x, operation.y, operation.color);
          break;
        }
        case "rect": {
          const region = validateRect(operation, skinSize);
          const color = normalizeRgbaHex(operation.color);
          const filled = operation.filled ?? true;
          for (let y = region.y; y < region.y + region.height; y += 1) {
            for (let x = region.x; x < region.x + region.width; x += 1) {
              if (
                filled ||
                x === region.x ||
                x === region.x + region.width - 1 ||
                y === region.y ||
                y === region.y + region.height - 1
              ) {
                write(x, y, color);
              }
            }
          }
          break;
        }
        case "line": {
          assertPoint(operation.x1, operation.y1, skinSize);
          assertPoint(operation.x2, operation.y2, skinSize);
          const color = normalizeRgbaHex(operation.color);
          for (const point of bresenham(
            operation.x1,
            operation.y1,
            operation.x2,
            operation.y2,
          )) {
            write(point.x, point.y, color);
          }
          break;
        }
        case "floodFill": {
          assertPoint(operation.x, operation.y, skinSize);
          const target = readPixel(pixels, operation.x, operation.y, skinSize);
          const replacement = normalizeRgbaHex(operation.color);
          if (target === replacement) break;
          const visited = new Uint8Array(skinSize * skinSize);
          const queue = new Uint16Array(skinSize * skinSize);
          let head = 0;
          let tail = 0;
          const enqueue = (x: number, y: number): void => {
            const index = y * skinSize + x;
            if (visited[index] || readPixel(pixels, x, y, skinSize) !== target)
              return;
            visited[index] = 1;
            queue[tail] = index;
            tail += 1;
          };
          enqueue(operation.x, operation.y);
          while (head < tail) {
            const index = queue[head];
            head += 1;
            const x = index % skinSize;
            const y = Math.floor(index / skinSize);
            write(x, y, replacement);
            if (x > 0) enqueue(x - 1, y);
            if (x < skinSize - 1) enqueue(x + 1, y);
            if (y > 0) enqueue(x, y - 1);
            if (y < skinSize - 1) enqueue(x, y + 1);
          }
          break;
        }
        case "replace": {
          const from = normalizeRgbaHex(operation.from);
          const to = normalizeRgbaHex(operation.to);
          const region = operation.region
            ? validateRect(operation.region, skinSize)
            : fullSkinRect(skinSize);
          if (from === to) break;
          for (let y = region.y; y < region.y + region.height; y += 1) {
            for (let x = region.x; x < region.x + region.width; x += 1) {
              if (readPixel(pixels, x, y, skinSize) === from) write(x, y, to);
            }
          }
          break;
        }
        case "mirror": {
          if (operation.surface && operation.region) {
            throw new SkinOperationError(
              "镜像操作不能同时指定 surface 和 region。",
            );
          }
          if (operation.surface) {
            const size = getSurfaceSize(
              operation.surface,
              document.model,
              skinSize,
            );
            const snapshot = new Uint8ClampedArray(pixels);
            for (let y = 0; y < size.height; y += 1) {
              for (let x = 0; x < size.width; x += 1) {
                const sourceX =
                  operation.axis === "horizontal" ? size.width - 1 - x : x;
                const sourceY =
                  operation.axis === "vertical" ? size.height - 1 - y : y;
                const source = surfaceToUv(
                  operation.surface,
                  sourceX,
                  sourceY,
                  document.model,
                  skinSize,
                );
                const target = surfaceToUv(
                  operation.surface,
                  x,
                  y,
                  document.model,
                  skinSize,
                );
                write(
                  target.x,
                  target.y,
                  readPixel(snapshot, source.x, source.y, skinSize),
                );
              }
            }
          } else {
            const region = operation.region
              ? validateRect(operation.region, skinSize)
              : fullSkinRect(skinSize);
            const snapshot = new Uint8ClampedArray(pixels);
            for (let y = 0; y < region.height; y += 1) {
              for (let x = 0; x < region.width; x += 1) {
                const sourceX =
                  region.x +
                  (operation.axis === "horizontal" ? region.width - 1 - x : x);
                const sourceY =
                  region.y +
                  (operation.axis === "vertical" ? region.height - 1 - y : y);
                write(
                  region.x + x,
                  region.y + y,
                  readPixel(snapshot, sourceX, sourceY, skinSize),
                );
              }
            }
          }
          break;
        }
        case "surfacePixel": {
          const uv = surfaceToUv(
            operation.surface,
            operation.x,
            operation.y,
            document.model,
            skinSize,
          );
          write(uv.x, uv.y, operation.color);
          break;
        }
        case "surfaceRect": {
          const size = getSurfaceSize(
            operation.surface,
            document.model,
            skinSize,
          );
          const region = validateSurfaceRect(operation, size);
          const color = normalizeRgbaHex(operation.color);
          const filled = operation.filled ?? true;
          for (let y = region.y; y < region.y + region.height; y += 1) {
            for (let x = region.x; x < region.x + region.width; x += 1) {
              if (
                filled ||
                x === region.x ||
                x === region.x + region.width - 1 ||
                y === region.y ||
                y === region.y + region.height - 1
              ) {
                const uv = surfaceToUv(
                  operation.surface,
                  x,
                  y,
                  document.model,
                  skinSize,
                );
                write(uv.x, uv.y, color);
              }
            }
          }
          break;
        }
        case "surfaceLine": {
          const size = getSurfaceSize(
            operation.surface,
            document.model,
            skinSize,
          );
          assertSurfacePoint(operation.x1, operation.y1, size);
          assertSurfacePoint(operation.x2, operation.y2, size);
          const color = normalizeRgbaHex(operation.color);
          for (const point of bresenham(
            operation.x1,
            operation.y1,
            operation.x2,
            operation.y2,
          )) {
            const uv = surfaceToUv(
              operation.surface,
              point.x,
              point.y,
              document.model,
              skinSize,
            );
            write(uv.x, uv.y, color);
          }
          break;
        }
        case "surfaceFloodFill": {
          const size = getSurfaceSize(
            operation.surface,
            document.model,
            skinSize,
          );
          assertSurfacePoint(operation.x, operation.y, size);
          const start = surfaceToUv(
            operation.surface,
            operation.x,
            operation.y,
            document.model,
            skinSize,
          );
          const target = readPixel(pixels, start.x, start.y, skinSize);
          const replacement = normalizeRgbaHex(operation.color);
          if (target === replacement) break;
          const visited = new Uint8Array(size.width * size.height);
          const queue = new Uint16Array(size.width * size.height);
          let head = 0;
          let tail = 0;
          const enqueue = (localX: number, localY: number): void => {
            if (
              localX < 0 ||
              localY < 0 ||
              localX >= size.width ||
              localY >= size.height
            ) {
              return;
            }
            const index = localY * size.width + localX;
            if (visited[index]) return;
            const uv = surfaceToUv(
              operation.surface,
              localX,
              localY,
              document.model,
              skinSize,
            );
            if (readPixel(pixels, uv.x, uv.y, skinSize) !== target) return;
            visited[index] = 1;
            queue[tail] = index;
            tail += 1;
          };
          enqueue(operation.x, operation.y);
          while (head < tail) {
            const index = queue[head];
            head += 1;
            const localX = index % size.width;
            const localY = Math.floor(index / size.width);
            const uv = surfaceToUv(
              operation.surface,
              localX,
              localY,
              document.model,
              skinSize,
            );
            write(uv.x, uv.y, replacement);
            enqueue(localX - 1, localY);
            enqueue(localX + 1, localY);
            enqueue(localX, localY - 1);
            enqueue(localX, localY + 1);
          }
          break;
        }
        case "surfaceReplace": {
          const size = getSurfaceSize(
            operation.surface,
            document.model,
            skinSize,
          );
          const region = operation.region
            ? validateSurfaceRect(operation.region, size)
            : { x: 0, y: 0, width: size.width, height: size.height };
          const from = normalizeRgbaHex(operation.from);
          const to = normalizeRgbaHex(operation.to);
          if (from === to) break;
          for (let y = region.y; y < region.y + region.height; y += 1) {
            for (let x = region.x; x < region.x + region.width; x += 1) {
              const uv = surfaceToUv(
                operation.surface,
                x,
                y,
                document.model,
                skinSize,
              );
              if (readPixel(pixels, uv.x, uv.y, skinSize) === from)
                write(uv.x, uv.y, to);
            }
          }
          break;
        }
        case "copySurface": {
          copySurfacePixels(
            pixels,
            write,
            document.model,
            skinSize,
            operation.from,
            operation.to,
            operation.flipHorizontal ?? false,
            operation.flipVertical ?? false,
          );
          break;
        }
        case "copyPart": {
          const layers = resolveCopyLayers(operation.layers);
          const flipHorizontal =
            operation.flipHorizontal ??
            isLeftRightCounterpart(operation.fromPart, operation.toPart);
          for (const layer of layers) {
            for (const face of SKIN_FACES) {
              const fromFace = face;
              const toFace = flipHorizontal
                ? remapFaceForHorizontalMirror(face)
                : face;
              copySurfacePixels(
                pixels,
                write,
                document.model,
                skinSize,
                {
                  part: operation.fromPart,
                  layer,
                  face: fromFace,
                },
                {
                  part: operation.toPart,
                  layer,
                  face: toFace,
                },
                flipHorizontal,
                false,
              );
            }
          }
          break;
        }
        default:
          throw new SkinOperationError(
            `未知皮肤操作：${JSON.stringify(operation)}`,
          );
      }
    }
  } catch (error) {
    if (error instanceof SkinOperationError) throw error;
    if (error instanceof Error) throw new SkinOperationError(error.message);
    throw error;
  }

  if (changed.size === 0) return { document, affectedPixels: 0 };
  return {
    document: {
      pixels,
      model: document.model,
      size: document.size,
      revision: document.revision + 1,
    },
    affectedPixels: changed.size,
  };
}

function fullSkinRect(size: SkinSize): PixelRect {
  return { x: 0, y: 0, width: size, height: size };
}

function validateRect(region: PixelRect, size: SkinSize): PixelRect {
  const { x, y, width, height } = region;
  if (
    !Number.isInteger(x) ||
    !Number.isInteger(y) ||
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1 ||
    x < 0 ||
    y < 0 ||
    x + width > size ||
    y + height > size
  ) {
    throw new SkinOperationError(
      `矩形必须完全位于 ${size}×${size} 范围内且宽高为正整数：(${x}, ${y}, ${width}, ${height})`,
    );
  }
  return { x, y, width, height };
}

function assertPoint(x: number, y: number, size: SkinSize): void {
  if (
    !Number.isInteger(x) ||
    !Number.isInteger(y) ||
    x < 0 ||
    y < 0 ||
    x >= size ||
    y >= size
  ) {
    throw new SkinOperationError(
      `像素坐标超出 ${size}×${size} 范围：(${x}, ${y})`,
    );
  }
}

function assertSurfacePoint(
  x: number,
  y: number,
  size: { width: number; height: number },
): void {
  if (
    !Number.isInteger(x) ||
    !Number.isInteger(y) ||
    x < 0 ||
    y < 0 ||
    x >= size.width ||
    y >= size.height
  ) {
    throw new SkinOperationError(
      `面内坐标超出 ${size.width}×${size.height} 范围：(${x}, ${y})`,
    );
  }
}

function validateSurfaceRect(
  region: PixelRect,
  size: { width: number; height: number },
): PixelRect {
  const { x, y, width, height } = region;
  if (
    !Number.isInteger(x) ||
    !Number.isInteger(y) ||
    !Number.isInteger(width) ||
    !Number.isInteger(height) ||
    width < 1 ||
    height < 1 ||
    x < 0 ||
    y < 0 ||
    x + width > size.width ||
    y + height > size.height
  ) {
    throw new SkinOperationError(
      `面内矩形必须完全位于 ${size.width}×${size.height} 范围内且宽高为正整数：(${x}, ${y}, ${width}, ${height})`,
    );
  }
  return { x, y, width, height };
}

function copySurfacePixels(
  pixels: Uint8ClampedArray,
  write: (x: number, y: number, color: RgbaHex) => void,
  model: SkinDocument["model"],
  skinSize: SkinSize,
  from: SurfaceRef,
  to: SurfaceRef,
  flipHorizontal: boolean,
  flipVertical: boolean,
): void {
  const fromSize = getSurfaceSize(from, model, skinSize);
  const toSize = getSurfaceSize(to, model, skinSize);
  if (fromSize.width !== toSize.width || fromSize.height !== toSize.height) {
    throw new SkinOperationError(
      `复制面尺寸不一致：${from.part}/${from.layer}/${from.face} 为 ${fromSize.width}×${fromSize.height}，${to.part}/${to.layer}/${to.face} 为 ${toSize.width}×${toSize.height}。`,
    );
  }
  const snapshot = new Uint8ClampedArray(pixels);
  for (let y = 0; y < toSize.height; y += 1) {
    for (let x = 0; x < toSize.width; x += 1) {
      const sourceX = flipHorizontal ? fromSize.width - 1 - x : x;
      const sourceY = flipVertical ? fromSize.height - 1 - y : y;
      const source = surfaceToUv(from, sourceX, sourceY, model, skinSize);
      const target = surfaceToUv(to, x, y, model, skinSize);
      write(
        target.x,
        target.y,
        readPixel(snapshot, source.x, source.y, skinSize),
      );
    }
  }
}

function resolveCopyLayers(
  layers: "inner" | "outer" | "both" | undefined,
): SkinLayer[] {
  if (layers === "inner") return ["inner"];
  if (layers === "outer") return ["outer"];
  return ["inner", "outer"];
}

function isLeftRightCounterpart(fromPart: SkinPart, toPart: SkinPart): boolean {
  return (
    (fromPart === "rightArm" && toPart === "leftArm") ||
    (fromPart === "leftArm" && toPart === "rightArm") ||
    (fromPart === "rightLeg" && toPart === "leftLeg") ||
    (fromPart === "leftLeg" && toPart === "rightLeg")
  );
}

function remapFaceForHorizontalMirror(face: SkinFace): SkinFace {
  if (face === "left") return "right";
  if (face === "right") return "left";
  return face;
}

function readPixel(
  pixels: Uint8ClampedArray,
  x: number,
  y: number,
  size: SkinSize,
): RgbaHex {
  const offset = pixelOffset(x, y, size);
  return rgbaTupleToHex(
    pixels[offset],
    pixels[offset + 1],
    pixels[offset + 2],
    pixels[offset + 3],
  );
}

function bresenham(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
): Array<{ x: number; y: number }> {
  const points: Array<{ x: number; y: number }> = [];
  let x = x1;
  let y = y1;
  const deltaX = Math.abs(x2 - x1);
  const stepX = x1 < x2 ? 1 : -1;
  const deltaY = -Math.abs(y2 - y1);
  const stepY = y1 < y2 ? 1 : -1;
  let error = deltaX + deltaY;

  while (true) {
    points.push({ x, y });
    if (x === x2 && y === y2) break;
    const doubleError = 2 * error;
    if (doubleError >= deltaY) {
      error += deltaY;
      x += stepX;
    }
    if (doubleError <= deltaX) {
      error += deltaX;
      y += stepY;
    }
  }
  return points;
}

/** Convenience helper for callers that need a single read without allocating a document. */
export function operationPixelAt(
  document: SkinDocument,
  x: number,
  y: number,
): RgbaHex {
  return getPixel(document, x, y);
}

/** Convenience helper used by procedural skin generators. */
export function paintRect(
  pixels: Uint8ClampedArray,
  region: PixelRect,
  color: RgbaHex,
): void {
  const size = inferSkinSize(pixels);
  if (!size)
    throw new SkinOperationError(
      `无法从 ${pixels.length} 个 RGBA 通道推断皮肤尺寸`,
    );
  validateRect(region, size);
  for (let y = region.y; y < region.y + region.height; y += 1) {
    for (let x = region.x; x < region.x + region.width; x += 1)
      setPixel(pixels, x, y, color);
  }
}
